import React, { useState, useEffect, useContext, useRef } from "react";
import { Box, Button, Grid, GridItem, Divider, Text, HStack, useToast, Spinner, Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

const COMPANY_CODES = [
  "EBL", "EMX", "ENL", "ELPL", "ENZ", "EPC", "ESA", "EPE", "EPP", "EWPL", "EAU", "EPL", "EMN", "EPDR", "EUK", "EBT", "EMCT", "EUS", "AVL", "LPK", "MNPI", "MPI", "TII", "TIL", "TIM", "ZHL"
];

const companyOptions = COMPANY_CODES.map(code => ({ label: code, value: code }));

const VendorDetailsTab = () => {
  const toast = useToast();
  const { workflowProcess } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  
  // Buyer & Company Fetching States
  const [buyerName, setBuyerName] = useState("");
  const [buyerGstin, setBuyerGstin] = useState("");
  const [isFetchingCompany, setIsFetchingCompany] = useState(false);
  const [companyCodeOptions, setCompanyCodeOptions] = useState<any[]>(companyOptions);

  const [form, setForm] = useState({
    companyCode: null as any,
    searchVendorCode: "",
    searchPan: "",
    searchGstin: "",
    selectedVendor: null as any,
    vendorCode: "",
    vendorName: "",
    vendorAddress: "",
    vendorPan: "",
    vendorBankAccount: "",
    vendorIfscCode: "",
    vendorGstIn: "",
  });

  const updateForm = (updates: any) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const lastFetchedPanVendor = useRef<string | null>(null);

  useEffect(() => {
    const fetchPanAndBankDetails = async () => {
      const vCode = form.vendorCode;
      if (!vCode || lastFetchedPanVendor.current === vCode) return;

      lastFetchedPanVendor.current = vCode;

      try {
        const response: any = await appRequest("document", "getPanBankDetails", { vendorCode: vCode });
        if (response?.status === "success" && response?.data) {
          setForm((prev: any) => ({
            ...prev,
            vendorPan: response.data.panNumber || prev.vendorPan || "",
            vendorBankAccount: response.data.bankAccountNumber || prev.vendorBankAccount || "",
            vendorIfscCode: response.data.bankIfscCode || prev.vendorIfscCode || "",
            vendorGstIn: response.data.vendorGstin || prev.vendorGstIn || "",
          }));
        }
      } catch (error) {
        console.error("Failed to load PAN and Bank Details", error);
      }
    };
    fetchPanAndBankDetails();
  }, [form.vendorCode]);

  const handleVendorSearch = async (autoQuery?: string) => {
    const query = autoQuery || form.searchVendorCode || form.searchPan || form.searchGstin;
    if (!query.trim()) {
      if (!autoQuery) {
        toast({
          title: "Missing Information",
          description: "Please enter a Vendor Code/Name.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
      return;
    }
    
    setLoading(true);
    try {
      let searchPayload: any = {};
      if (form.searchVendorCode?.trim()) searchPayload.vendorCode = form.searchVendorCode.trim();
      else if (form.searchPan?.trim()) searchPayload.pan = form.searchPan.trim();
      else if (form.searchGstin?.trim()) searchPayload.gstin = form.searchGstin.trim();

      // Directly call getPanBankDetails
      const panBankRes: any = await appRequest("document", "getPanBankDetails", searchPayload);
      
      if (panBankRes?.status === "success" && panBankRes?.data) {
        const panData = panBankRes.data;
        const mappedVendor = {
          label: `${panData.vendorCode || ""} -> PAN: ${panData.panNumber || ""}`,
          value: panData.vendorCode,
          vendorData: {
            vendorCode: panData.vendorCode,
            supplierName: panData.vendorName || "", 
            vendorAddress: "",
            panNumber: panData.panNumber,
            bankAccountNumber: panData.bankAccountNumber,
            bankIfscCode: panData.bankIfscCode,
            vendorGstin: panData.vendorGstin
          }
        };

        const options = [mappedVendor];
        setVendorOptions(options);
        
        // Auto-select the first vendor option
        const firstOption = options[0];
        updateForm({
          selectedVendor: firstOption,
          vendorCode: firstOption.vendorData.vendorCode || "",
          vendorName: firstOption.vendorData.supplierName || "",
          vendorAddress: "",
          vendorPan: firstOption.vendorData.panNumber || "",
          vendorBankAccount: firstOption.vendorData.bankAccountNumber || "",
          vendorIfscCode: firstOption.vendorData.bankIfscCode || "",
          vendorGstIn: firstOption.vendorData.vendorGstin || ""
        });
        
        toast({ title: "Vendor details found", status: "success", duration: 2000 });
      } else {
        setVendorOptions([]);
        if (!autoQuery) {
          toast({ title: "Vendor not found", status: "error", duration: 2000 });
        }
      }
    } catch (error) {
      console.error(error);
      if (!autoQuery) {
        toast({ title: "Error fetching vendor", status: "error", duration: 2000 });
      }
    } finally {
      setLoading(false);
    }
  };

  // Automatic Fetching of Vendor Code based on Company and Buyer GSTIN/Name
  useEffect(() => {
    if (!form.companyCode?.value) return;
    if (!buyerName.trim() && !buyerGstin.trim()) return;

    const fetchGstVendorCode = async () => {
      setLoading(true);
      try {
        const payload = {
          companyCode: form.companyCode?.value,
          type: "getGstVendorCode",
          gstin: buyerGstin.trim() || buyerName.trim(),
          ...workflowProcess,
        };
        const res: any = await appRequest("user", "masterFinancialApi", payload);
        
        if (res?.status === "success" && Array.isArray(res?.data)) {
          const options = res.data.map((it: any) => ({
            label: `${it?.label || it?.vendorName || it?.vendorCode} -> ${it.vendorAddress || ""}`,
            value: it?.vendorCode,
            vendorData: it
          }));
          
          if (options.length === 0) {
            handleVendorSearch(buyerGstin.trim() || buyerName.trim());
          } else if (options.length > 0) {
            // Fetch PAN and Bank details for auto-fetched vendors
            const enhancedData = await Promise.all(options.map(async (opt: any) => {
              try {
                const panBankRes: any = await appRequest("document", "getPanBankDetails", { vendorCode: opt.value });
                if (panBankRes?.status === "success" && panBankRes?.data) {
                  return { ...opt, vendorData: { ...opt.vendorData, ...panBankRes.data } };
                }
              } catch(e) {}
              return opt;
            }));

            setVendorOptions(enhancedData);
            const first = enhancedData[0];
            updateForm({
              selectedVendor: first,
              vendorCode: first.vendorData?.vendorCode || first.value || "",
              vendorName: first.vendorData?.supplierName || first.vendorData?.label || first.vendorData?.vendorName || "",
              vendorAddress: first.vendorData?.vendorAddress || "",
              vendorPan: first.vendorData?.panNumber || first.vendorData?.PAN_Number || first.vendorData?.pan_no || "",
              vendorBankAccount: first.vendorData?.bankAccountNumber || first.vendorData?.bankAccount || first.vendorData?.bank_account_no || "",
              vendorIfscCode: first.vendorData?.bankIfscCode || first.vendorData?.ifscCode || first.vendorData?.ifsc_code || "",
              vendorGstIn: first.vendorData?.vendorGstin || first.vendorData?.gstin || first.vendorData?.gst_no || first.vendorData?.GSTIN || ""
            });
            toast({ title: "Vendors auto-fetched", status: "success", duration: 2000 });
          }
        } else {
          setVendorOptions([]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchGstVendorCode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.companyCode, buyerName, buyerGstin, workflowProcess]);

  const handleFetchCompanyCodes = async () => {
    if (!buyerName.trim() && !buyerGstin.trim()) {
      toast({ title: "Enter Buyer Name or GSTIN", status: "warning", duration: 3000 });
      return;
    }
    setIsFetchingCompany(true);
    try {
      const payload = {
        buyerName,
        buyerGstin,
        type: "getCompanyCode",
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res?.data)) {
        const options = res.data.map((item: any) => ({ label: item, value: item }));
        setCompanyCodeOptions(options);
        if (options.length > 0) {
          updateForm({ companyCode: options[0] });
        }
        toast({ title: "Company Codes fetched successfully", status: "success", duration: 3000 });
      } else {
        setCompanyCodeOptions([]);
      }
    } catch (error: any) {
      toast({ title: "Failed to fetch Company Codes", description: error?.message, status: "error" });
    } finally {
      setIsFetchingCompany(false);
    }
  };



  const handleReset = () => {
    setBuyerName("");
    setBuyerGstin("");
    setCompanyCodeOptions(companyOptions);
    setVendorOptions([]);
    setForm({
      companyCode: null as any,
      searchVendorCode: "",
      searchPan: "",
      searchGstin: "",
      selectedVendor: null as any,
      vendorCode: "",
      vendorName: "",
      vendorAddress: "",
      vendorPan: "",
      vendorBankAccount: "",
      vendorIfscCode: "",
      vendorGstIn: "",
    });
  };

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Button 
          colorScheme="red" 
          variant="outline"
          size="sm" 
          px={6} 
          onClick={handleReset} 
          shadow="sm" 
          _hover={{ bg: "red.50", transform: "translateY(-1px)", shadow: "md" }} 
          transition="all 0.2s"
          borderRadius="md"
          fontWeight="bold"
        >
          Reset Details
        </Button>
      </Box>

      {/* Buyer & Company Context Section */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" mb={6} borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={5} borderBottom="2px solid" borderColor="blue.100" pb={2}>
          Buyer & Company Context
        </Text>
        <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={6} alignItems="end">
          <CustomInput
            name="buyerName"
            label="Buyer Name"
            value={buyerName}
            onChange={(e: any) => setBuyerName(e.target.value)}
          />
          <CustomInput
            name="buyerGstin"
            label="Buyer GSTIN"
            value={buyerGstin}
            onChange={(e: any) => setBuyerGstin(e.target.value)}
          />
          <Button colorScheme="blue" size="md" onClick={handleFetchCompanyCodes} isLoading={isFetchingCompany} mb={1} borderRadius="md" _hover={{ bg: "blue.600" }}>
            Fetch Company Codes
          </Button>
        </Grid>
        
        <Box mt={6} w={{ base: "100%", md: "32%" }}>
          <CustomInput
            name="companyCode"
            label="Company Code"
            type="select"
            isBoxSelect
            options={companyCodeOptions}
            value={form.companyCode}
            onChange={(val: any) => updateForm({ companyCode: val })}
          />
        </Box>
      </Box>

      {/* Vendor Search Section */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" mb={6} borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={5} borderBottom="2px solid" borderColor="blue.100" pb={2}>
          Vendor Search
        </Text>
        <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 1fr 1fr 1fr" }} gap={6} alignItems="end">
          <CustomInput
            name="searchVendorCode"
            label="Vendor Code"
            value={form.searchVendorCode}
            onChange={(e: any) => updateForm({ searchVendorCode: e.target.value })}
          />
          <CustomInput
            name="searchPan"
            label="PAN Details"
            value={form.searchPan}
            onChange={(e: any) => updateForm({ searchPan: e.target.value })}
          />
          <CustomInput
            name="searchGstin"
            label="GSTIN"
            value={form.searchGstin}
            onChange={(e: any) => updateForm({ searchGstin: e.target.value })}
          />
          <Button colorScheme="blue" size="md" px={8} onClick={() => handleVendorSearch()} isLoading={loading} mb={1} borderRadius="md" _hover={{ bg: "blue.600" }} width="100%">
            Search
          </Button>
        </Grid>
      </Box>

      {/* Vendor Information Section */}
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" shadow="sm" borderColor="gray.200">
        <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={5} borderBottom="2px solid" borderColor="blue.100" pb={2}>
          Vendor Information
        </Text>
        <Box overflowX="auto">
          <Table variant="simple" size="sm" colorScheme="blue">
            <Thead bg="gray.50">
              <Tr>
                <Th py={3} color="gray.600">Vendor Code</Th>
                <Th py={3} color="gray.600">Vendor Name</Th>
                <Th py={3} color="gray.600">Company Code</Th>
                <Th py={3} color="gray.600">Bank Account Number</Th>
                <Th py={3} color="gray.600">IFSC Code</Th>
                <Th py={3} color="gray.600">PAN Number</Th>
                <Th py={3} color="gray.600">GST Number</Th>
              </Tr>
            </Thead>
            <Tbody>
              {vendorOptions.map((opt: any, idx: number) => {
                const v = opt.vendorData || {};
                const isSelected = form.vendorCode === (v.vendorCode || opt.value);
                const panStr = v.panNumber || v.PAN_Number || v.pan_no || v.pan || "-";
                const companyCodeStr = Array.isArray(v.companyCode) ? v.companyCode.join(", ") : (v.companyCode || "-");
                const bankAccStr = v.bankAccountNumber || v.bankAccount || v.bank_account_no || v.bankAccountNo || "-";
                const ifscStr = v.bankIfscCode || v.ifscCode || v.ifsc_code || v.ifsc || "-";
                const gstStr = v.vendorGstin || v.gstin || v.gst_no || v.GSTIN || "-";
                
                return (
                  <Tr 
                    key={idx} 
                    cursor="pointer" 
                    bg={isSelected ? "blue.50" : "transparent"}
                    _hover={{ bg: "gray.50" }}
                    onClick={async () => {
                      let finalV = { ...v };
                      // Fetch bank details dynamically when clicked if they don't already have them
                      if (!finalV.bankAccountNumber && !finalV.bankAccount) {
                        try {
                          const panBankRes: any = await appRequest("document", "getPanBankDetails", { vendorCode: finalV.vendorCode || opt.value });
                          if (panBankRes?.status === "success" && panBankRes?.data) {
                            finalV = { ...finalV, ...panBankRes.data };
                            // Update the vendorData in options so it reflects in the table immediately
                            opt.vendorData = finalV;
                            setVendorOptions([...vendorOptions]);
                          }
                        } catch(e) {}
                      }

                      const panStrUpdated = finalV.panNumber || finalV.PAN_Number || finalV.pan_no || finalV.pan || "";
                      const bankAccStrUpdated = finalV.bankAccountNumber || finalV.bankAccount || finalV.bank_account_no || finalV.bankAccountNo || "";
                      const ifscStrUpdated = finalV.bankIfscCode || finalV.ifscCode || finalV.ifsc_code || finalV.ifsc || "";
                      const gstStrUpdated = finalV.vendorGstin || finalV.gstin || finalV.gst_no || finalV.GSTIN || "";

                      updateForm({ 
                        selectedVendor: opt,
                        vendorCode: finalV.vendorCode || opt.value || "",
                        vendorName: finalV.supplierName || finalV.label || finalV.vendorName || "",
                        vendorAddress: finalV.vendorAddress || "",
                        vendorPan: panStrUpdated,
                        vendorBankAccount: bankAccStrUpdated,
                        vendorIfscCode: ifscStrUpdated,
                        vendorGstIn: gstStrUpdated
                      });
                    }}
                  >
                    <Td py={3} fontWeight={isSelected ? "bold" : "normal"} color={isSelected ? "blue.600" : "inherit"}>
                      {v.vendorCode || opt.value || "-"}
                    </Td>
                    <Td py={3}>{v.vendorName || v.supplierName || "-"}</Td>
                    <Td py={3}>{companyCodeStr}</Td>
                    <Td py={3}>{bankAccStr}</Td>
                    <Td py={3}>{ifscStr}</Td>
                    <Td py={3}>{panStr}</Td>
                    <Td py={3}>{gstStr}</Td>
                  </Tr>
                );
              })}
              {vendorOptions.length === 0 && (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                    No vendor information to display. Please search above.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default VendorDetailsTab;
